import uid from "common/utils/uid";
import type { DocMigration } from "../docMigration";

const defaultMonolithicAssemblyId = "defaultMonolithicAssembly";
const defaultCIPStemWallAssemblyId = "defaultCIPStemWallAssembly";
const defaultCMUStemWallAssemblyId = "defaultCMUStemWallAssembly";
const defaultIsolatedCircularAssemblyId = "defaultIsolatedCircularAssembly";
const defaultIsolatedRectangularAssemblyId = "defaultIsolatedRectangularAssembly";

interface DeepEqualOptions {
  readonly omit?: { [key: string]: boolean };
  readonly ignoreUndefined?: boolean;
  readonly ignoreNullish?: boolean;
}

export function deepequalWithOptions(a: any, b: any, options?: DeepEqualOptions) {
  if (a === b) {
    return true;
  }

  if (a && b && typeof a === "object" && typeof b === "object") {
    if (a.constructor !== b.constructor) {
      return false;
    }

    if (a.constructor === RegExp) {
      return a.source === b.source && a.flags === b.flags;
    }

    const { omit = {}, ignoreUndefined = false, ignoreNullish = false } = options ?? {};

    const aCopy = { ...a };
    const bCopy = { ...b };

    for (const key in omit) {
      delete aCopy[key];
      delete bCopy[key];
    }

    a = aCopy;
    b = bCopy;

    let length, i;
    if (Array.isArray(a) || (ArrayBuffer.isView(a) && !(a instanceof DataView))) {
      // TypedArrays all have defined length, but ts thinks a and b might have some other type.
      length = (a as any[]).length;
      if (length !== (b as any[]).length) {
        return false;
      }
      for (i = length; i-- !== 0; ) {
        if (!deepequalWithOptions((a as any[])[i], (b as any[])[i], options)) {
          return false;
        }
      }
      return true;
    }

    if (a.valueOf !== Object.prototype.valueOf) {
      return a.valueOf() === b.valueOf();
    }
    if (a.toString !== Object.prototype.toString) {
      return a.toString() === b.toString();
    }

    let aKeys = ignoreUndefined ? Object.keys(a).filter((key) => a[key] !== undefined) : Object.keys(a);
    let bKeys = ignoreUndefined ? Object.keys(b).filter((key) => b[key] !== undefined) : Object.keys(b);

    if (ignoreNullish) {
      aKeys = aKeys.filter(
        (key) =>
          a[key] !== undefined &&
          a[key] !== null &&
          !(typeof a[key] === "string" && a[key] === "") &&
          !(typeof a[key] === "boolean" && a[key] === false),
      );
      bKeys = bKeys.filter(
        (key) =>
          b[key] !== undefined &&
          b[key] !== null &&
          !(typeof b[key] === "string" && b[key] === "") &&
          !(typeof b[key] === "boolean" && b[key] === false),
      );
    }

    length = aKeys.length;
    if (length !== bKeys.length) {
      return false;
    }

    for (i = length; i-- !== 0; ) {
      if (!Object.prototype.hasOwnProperty.call(b, aKeys[i])) {
        return false;
      }
    }

    for (i = length; i-- !== 0; ) {
      const key = aKeys[i];
      if (!deepequalWithOptions(a[key], b[key], options)) {
        return false;
      }
    }

    return true;
  }

  // true if both NaN, false otherwise
  return a !== a && b !== b;
}

const defaultFoundationAssembliesInternal = {
  [defaultMonolithicAssemblyId]: {
    entityType: "FOUNDATION_ASSEMBLY" as const,
    id: defaultMonolithicAssemblyId,
    name: "Monolithic Foundation",
    assemblySystem: "MONOLITHIC" as const,
    footingId: "defaultTurndownFooting",
    stemWallId: undefined,
    pierId: undefined,
    sortIndex: 0,
  },
  [defaultCIPStemWallAssemblyId]: {
    entityType: "FOUNDATION_ASSEMBLY" as const,
    id: defaultCIPStemWallAssemblyId,
    name: "CIP Stem Wall Foundation",
    assemblySystem: "STEM_WALL_CIP" as const,
    footingId: "defaultStripFooting",
    stemWallId: "defaultCIPStemWall",
    pierId: undefined,
    sortIndex: 0,
  },
  [defaultCMUStemWallAssemblyId]: {
    entityType: "FOUNDATION_ASSEMBLY" as const,
    id: defaultCMUStemWallAssemblyId,
    name: "CMU Stem Wall Foundation",
    assemblySystem: "STEM_WALL_CMU" as const,
    footingId: "defaultStripFooting",
    stemWallId: "defaultCMUStemWall",
    pierId: undefined,
    sortIndex: 0,
  },
  [defaultIsolatedCircularAssemblyId]: {
    entityType: "FOUNDATION_ASSEMBLY" as const,
    id: defaultIsolatedCircularAssemblyId,
    name: "Isolated Circular Pier Foundation",
    assemblySystem: "ISOLATED" as const,
    footingId: "defaultCircularIsolatedFooting",
    stemWallId: undefined,
    pierId: "defaultCircularCIPPier",
    sortIndex: 0,
  },
  [defaultIsolatedRectangularAssemblyId]: {
    entityType: "FOUNDATION_ASSEMBLY" as const,
    id: defaultIsolatedRectangularAssemblyId,
    name: "Isolated Rectangular Pier Foundation",
    assemblySystem: "ISOLATED" as const,
    footingId: "defaultRectangularIsolatedFooting",
    stemWallId: undefined,
    pierId: "defaultRectangularCIPPier",
    sortIndex: 0,
  },
} as const;

const builtInFoundationAssemblies: { [id: string]: any } = defaultFoundationAssembliesInternal;

const defaultStripFootingId = "defaultStripFooting";
const defaultTurndownFootingId = "defaultTurndownFooting";
const defaultCircularIsolatedFootingId = "defaultCircularIsolatedFooting";
const defaultRectangularIsolatedFootingId = "defaultRectangularIsolatedFooting";
const defaultCIPStemWallId = "defaultCIPStemWall";
const defaultCMUStemWallId = "defaultCMUStemWall";
const defaultCircularCIPPierId = "defaultCircularCIPPier";
const defaultRectangularCIPPierId = "defaultRectangularCIPPier";
const defaultRectangularCMUPierId = "defaultRectangularCMUPier";
const defaultCurbId = "defaultCurb";

const defaultFoundationComponentsInternal = {
  [defaultStripFootingId]: {
    entityType: "FOUNDATION_COMPONENT" as const,
    id: defaultStripFootingId,
    sortIndex: 0,
    componentType: "FOOTING" as const,
    footingType: "STRIP" as const,
    name: '18"x8" Strip Footing w/ AUTO 4" Brick Padding',
    footingWidth: 18,
    footingDepth: 8,
    footingPadding: 4,
  },
  [defaultTurndownFootingId]: {
    entityType: "FOUNDATION_COMPONENT" as const,
    id: defaultTurndownFootingId,
    sortIndex: 0,
    componentType: "FOOTING" as const,
    footingType: "TURNDOWN" as const,
    name: '18" Turndown Footing w/ AUTO 5.5" Ledge',
    footingWidth: 18,
    brickLedgeBehavior: "AUTO" as const,
    brickLedgeType: "ADD" as const,
    brickLedgeWidth: 5.5,
  },
  [defaultCircularIsolatedFootingId]: {
    entityType: "FOUNDATION_COMPONENT" as const,
    id: defaultCircularIsolatedFootingId,
    sortIndex: 0,
    componentType: "FOOTING" as const,
    footingType: "CIRCULAR_ISOLATED" as const,
    name: '16" Diameter Circular Isolated Footing',
    footingRadius: 8,
    footingDepth: 6,
    material: undefined,
  },
  [defaultRectangularIsolatedFootingId]: {
    entityType: "FOUNDATION_COMPONENT" as const,
    id: defaultRectangularIsolatedFootingId,
    sortIndex: 0,
    componentType: "FOOTING" as const,
    footingType: "RECTANGULAR_ISOLATED" as const,
    name: '16"x16" Rectangular Isolated Footing',
    footingLength: 16,
    footingWidth: 16,
    footingDepth: 6,
    material: undefined,
  },
  [defaultCIPStemWallId]: {
    entityType: "FOUNDATION_COMPONENT" as const,
    id: defaultCIPStemWallId,
    sortIndex: 0,
    componentType: "STEMWALL" as const,
    stemWallType: "CIP" as const,
    name: '8" CIP Stem Wall w/ AUTO 5.5" Ledge',
    stemWallWidth: 8,
    brickLedgeBehavior: "AUTO" as const,
    brickLedgeType: "ADD" as const,
    brickLedgeWidth: 5.5,
  },
  [defaultCMUStemWallId]: {
    entityType: "FOUNDATION_COMPONENT" as const,
    id: defaultCMUStemWallId,
    sortIndex: 0,
    componentType: "STEMWALL" as const,
    stemWallType: "CMU" as const,
    name: '8" CMU Stem Wall w/ AUTO 4" Ledge',
    stemWallBlockWidth: 8,
    brickLedgeBehavior: "AUTO" as const,
    brickLedgeType: "ADD" as const,
    stemWallBrickLedgeBlockWidth: 12,
  },
  [defaultCurbId]: {
    entityType: "FOUNDATION_COMPONENT" as const,
    id: defaultCurbId,
    sortIndex: 0,
    componentType: "CURB" as const,
    curbType: "AUTO",
    name: "Auto Curb",
  },
  [defaultCircularCIPPierId]: {
    entityType: "FOUNDATION_COMPONENT" as const,
    id: defaultCircularCIPPierId,
    sortIndex: 0,
    componentType: "PIER" as const,
    pierType: "CIRCULAR_CIP" as const,
    name: '8" Diameter Circular CIP Pier',
    pierRadius: 4,
    pierDepth: undefined,
    material: undefined,
  },
  [defaultRectangularCIPPierId]: {
    entityType: "FOUNDATION_COMPONENT" as const,
    id: defaultRectangularCIPPierId,
    sortIndex: 0,
    componentType: "PIER" as const,
    pierType: "RECTANGULAR_CIP" as const,
    name: '8" x 8" Rectangular CIP Pier',
    pierLength: 8,
    pierWidth: 8,
    pierDepth: undefined,
    material: undefined,
  },
  [defaultRectangularCMUPierId]: {
    entityType: "FOUNDATION_COMPONENT" as const,
    id: defaultRectangularCMUPierId,
    sortIndex: 0,
    componentType: "PIER" as const,
    pierType: "RECTANGULAR_CMU" as const,
    name: '8" x 8" Rectangular CMU Pier',
    pierLength: 8,
    pierWidth: 8,
    pierDepth: undefined,
  },
} as const;

const builtInFoundationComponents: { [id: string]: any } = defaultFoundationComponentsInternal;

const defaultFoundationAssemblyIdMapping = {
  "MONO-FND-AUTO": defaultMonolithicAssemblyId,
  "STEM-CIP-FND-AUTO": defaultCIPStemWallAssemblyId,
  "STEM-CMU-FND-AUTO": defaultCMUStemWallAssemblyId,
  "CONC-PIER-FND-AUTO": defaultIsolatedCircularAssemblyId,
} as const;

const referenceKeys = ["footingId", "stemWallId", "pierId", "curbId"];

const DocEntityPrefix = {
  FoundationAssembly: "foundationAssembly",
  FoundationComponentEntity: "foundationComponentEntity",
  Group: "group",
} as const;

// eslint-disable-next-line @typescript-eslint/naming-convention
const migration_0_0_498: DocMigration = {
  maxSchemaVersion: "0.0.498",
  description: "Migrate foundation catalog settings to doc tables",
  apply(json: any): any {
    // Initialize groups and entityGroups if they don't exist
    if (!json.groups) {
      json.groups = {};
    }
    if (!json.entityGroups) {
      json.entityGroups = {};
    }
    // Initialize entityRefs if it doesn't exist
    if (!json.entityRefs) {
      json.entityRefs = {};
    }

    // Track which entities belong to which settings set
    const settingsSetToAssemblies = new Map<string, string[]>();
    const settingsSetToComponents = new Map<string, string[]>();
    json.foundationComponents = {
      ...builtInFoundationComponents,
      ...json.foundationComponents,
    };
    json.foundationAssemblies = {
      ...builtInFoundationAssemblies,
      ...json.foundationAssemblies,
    };
    for (const foundationComponentId in json.foundationComponents) {
      const foundationComponent = json.foundationComponents[foundationComponentId];
      if (foundationComponent.entityType === "FOUNDATION_COMPONENT_ENTITY") {
        json.foundationComponents[foundationComponentId] = {
          ...foundationComponent,
          entityType: "FOUNDATION_COMPONENT",
        };
      }
    }
    for (const oldDefaultFoundationId of Object.keys(defaultFoundationAssemblyIdMapping)) {
      delete json.foundationAssemblies[oldDefaultFoundationId];
    }

    const generateUniqueId = (oldId: string, settingsId: string, table: Record<string, any>): string => {
      let suffix = 0;
      let newId = `${oldId}_${settingsId}_${suffix}`;
      while (table[newId]) {
        suffix += 1;
        newId = `${oldId}_${settingsId}_${suffix}`;
      }
      return newId;
    };

    // First go through all foundation components and deduplicate them
    // This map is duplicate id to a list of [settingsSetId, newComponentId]
    const remappedFoundationComponents = new Map<string, [string, string][]>();
    for (const settingsId in json.settings) {
      const setting = json.settings[settingsId];
      if (setting.type !== "FOUNDATION_CATALOG_SETTINGS") {
        continue;
      }
      const settingsSetId = setting.parentId;
      // Extract docRef information from the settings set
      const docRefId = json.entityRefs[settingsSetId]?.docRefId;
      const docRefDisabled = json.entityRefs[settingsSetId]?.disabled;

      if (!settingsSetToComponents.has(settingsSetId)) {
        settingsSetToComponents.set(settingsSetId, []);
      }
      for (const componentId in setting.foundationComponents) {
        if (builtInFoundationComponents[componentId]) {
          continue;
        }
        const component = setting.foundationComponents[componentId];
        const componentEntity = {
          ...component,
          id: componentId,
          entityType: "FOUNDATION_COMPONENT",
        };
        let finalComponentId = componentId;
        if (json.foundationComponents[componentId]) {
          if (
            !deepequalWithOptions(json.foundationComponents[componentId], componentEntity, {
              omit: { name: true },
              ignoreUndefined: true,
            })
          ) {
            console.warn(
              `Foundation component with id ${componentId} already exists in foundationComponents. Generating new id.`,
            );
            // generate a new stable id for the component having an id collision
            const newComponentId = generateUniqueId(componentId, settingsId, json.foundationComponents);
            remappedFoundationComponents.set(componentId, [
              ...(remappedFoundationComponents.get(componentId) ?? []),
              [setting.parentId, newComponentId],
            ]);
            json.foundationComponents[newComponentId] = {
              ...component,
              id: newComponentId,
              entityType: "FOUNDATION_COMPONENT",
            };
            finalComponentId = newComponentId;
          }
        } else {
          json.foundationComponents[componentId] = componentEntity;
        }

        // If the settings were doc-ref'd, add the migrated component to entity refs
        if (docRefId) {
          json.entityRefs[finalComponentId] = {
            id: finalComponentId,
            docRefId,
            entityType: "FOUNDATION_COMPONENT",
            disabled: docRefDisabled,
          };
        }

        settingsSetToComponents.get(settingsSetId)?.push(finalComponentId);
      }
    }
    // Now go through all foundation assemblies and deduplicate them
    const remappedFoundationAssemblies = new Map<string, [string, string][]>();
    for (const settingsId in json.settings) {
      const setting = json.settings[settingsId];
      if (setting.type !== "FOUNDATION_CATALOG_SETTINGS") {
        continue;
      }
      const settingsSetId = setting.parentId;
      // Extract docRef information from the settings set
      const docRefId = json.entityRefs[settingsSetId]?.docRefId;
      const docRefDisabled = json.entityRefs[settingsSetId]?.disabled;

      if (!settingsSetToAssemblies.has(settingsSetId)) {
        settingsSetToAssemblies.set(settingsSetId, []);
      }
      for (const assemblyId in setting.foundationAssemblies) {
        if (Object.keys(defaultFoundationAssemblyIdMapping).includes(assemblyId)) {
          continue;
        }
        const assembly = setting.foundationAssemblies[assemblyId];
        const assemblyEntity = {
          ...assembly,
          id: assemblyId,
          entityType: "FOUNDATION_ASSEMBLY",
        };
        for (const key of referenceKeys) {
          const remaps = remappedFoundationComponents.get(assembly[key]) ?? [];
          for (const [settingsSetId, newComponentId] of remaps) {
            if (settingsSetId === setting.parentId) {
              assemblyEntity[key] = newComponentId;
              break;
            }
          }
        }
        let finalAssemblyId = assemblyId;
        if (json.foundationAssemblies[assemblyId]) {
          if (
            !deepequalWithOptions(json.foundationAssemblies[assemblyId], assemblyEntity, {
              omit: { name: true },
              ignoreUndefined: true,
            })
          ) {
            console.warn(
              `Foundation assembly with id ${assemblyId} already exists in foundationAssemblies. Generating new id.`,
            );
            // generate a new stable id for the assembly having an id collision
            const newAssemblyId = generateUniqueId(assemblyId, settingsId, json.foundationAssemblies);
            remappedFoundationAssemblies.set(assemblyId, [
              ...(remappedFoundationAssemblies.get(assemblyId) ?? []),
              [setting.parentId, newAssemblyId],
            ]);
            json.foundationAssemblies[newAssemblyId] = {
              ...assemblyEntity,
              id: newAssemblyId,
            };
            finalAssemblyId = newAssemblyId;
          }
        } else {
          json.foundationAssemblies[assemblyId] = assemblyEntity;
        }

        // If the settings were doc-ref'd, add the migrated assembly to entity refs
        if (docRefId) {
          json.entityRefs[finalAssemblyId] = {
            id: finalAssemblyId,
            docRefId,
            entityType: "FOUNDATION_ASSEMBLY",
            disabled: docRefDisabled,
            sortIndex: 0,
          };
        }

        settingsSetToAssemblies.get(settingsSetId)?.push(finalAssemblyId);
      }
      // Clean up entityRefs for the deleted settings
      delete json.settings[settingsId];
      delete json.entityRefs[settingsId];
    }

    // Go through all doc entities that might be referencing the old default or duplicate foundation assemblies and update them to the new default ids
    for (const settingsId in json.settings) {
      const settings = json.settings[settingsId];
      if (settings.type !== "FOUNDATION_SETTINGS") {
        continue;
      }
      if (!settings.foundationHash || !settings[settings.foundationHash]) {
        continue;
      }
      if (
        Object.keys(defaultFoundationAssemblyIdMapping).includes(settings[settings.foundationHash].foundationAssembly)
      ) {
        json.settings[settingsId][settings.foundationHash].foundationAssembly =
          defaultFoundationAssemblyIdMapping[settings[settings.foundationHash].foundationAssembly];
      }
      if (remappedFoundationAssemblies.has(settings[settings.foundationHash].foundationAssembly)) {
        const remaps = remappedFoundationAssemblies.get(settings[settings.foundationHash].foundationAssembly) ?? [];
        for (const [settingsSetId, newAssemblyId] of remaps) {
          if (settingsSetId === settings.parentId) {
            json.settings[settingsId][settings.foundationHash].foundationAssembly = newAssemblyId;
            break;
          }
        }
      }
    }

    // Make sure columns and edge behaviors have updated default foundation assembly ids
    // A prod query has revealed that there's no customer homes that have columns or edge behaviors that reference remapped foundation assemblies
    for (const columnId in json.columns) {
      const column = json.columns[columnId];
      if (Object.keys(defaultFoundationAssemblyIdMapping).includes(column.isolatedAssemblyId)) {
        json.columns[columnId].isolatedAssemblyId = defaultFoundationAssemblyIdMapping[column.isolatedAssemblyId];
      }
    }

    for (const edgeBehaviorId in json.edgeBehaviors) {
      const edgeBehavior = json.edgeBehaviors[edgeBehaviorId];
      if (
        edgeBehavior.edgeBehaviorType === "FOUNDATION_PATTERN" &&
        edgeBehavior.foundationPattern &&
        edgeBehavior.foundationPattern.patternType === "ISOLATED" &&
        Object.keys(defaultFoundationAssemblyIdMapping).includes(edgeBehavior.foundationPattern.assemblyId)
      ) {
        json.edgeBehaviors[edgeBehaviorId].foundationPattern.assemblyId =
          defaultFoundationAssemblyIdMapping[edgeBehavior.foundationPattern.assemblyId];
      }
    }

    // Create groups for foundation assemblies and components
    // Get all unique settings set IDs that have foundation entities
    const allSettingsSetIds = new Set([...settingsSetToAssemblies.keys(), ...settingsSetToComponents.keys()]);

    // Calculate the maximum sortIndex for groups
    let maxGroupSortIndex = 0;
    for (const groupId in json.groups) {
      const group = json.groups[groupId];
      if (group.sortIndex !== undefined && group.sortIndex > maxGroupSortIndex) {
        maxGroupSortIndex = group.sortIndex;
      }
    }

    for (const settingsSetId of allSettingsSetIds) {
      const settingsSet = json.settingsSets?.[settingsSetId];
      const settingsSetName = settingsSet?.name ?? "Untitled Settings Set";

      const assemblies = settingsSetToAssemblies.get(settingsSetId) ?? [];
      const components = settingsSetToComponents.get(settingsSetId) ?? [];

      // Create group for foundation assemblies if there are any
      if (assemblies.length > 0) {
        const assemblyGroupId = uid();
        maxGroupSortIndex += 1;
        const assemblyGroupSortIndex = maxGroupSortIndex;
        json.groups[assemblyGroupId] = {
          entityType: "GROUP",
          id: assemblyGroupId,
          name: `${settingsSetName}`,
          sortIndex: assemblyGroupSortIndex,
          parentId: undefined,
          disabled: false,
        };

        // Sort assemblies alphanumerically by name
        const sortedAssemblies = [...assemblies].sort((a, b) => {
          const nameA = json.foundationAssemblies[a]?.name ?? "";
          const nameB = json.foundationAssemblies[b]?.name ?? "";
          return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: "base" });
        });

        // Add entries to entityGroups and set sortIndex for each assembly
        for (let i = 0; i < sortedAssemblies.length; i++) {
          const assemblyId = sortedAssemblies[i];
          const assemblyHash = `${DocEntityPrefix.FoundationAssembly}.${assemblyId}`;
          json.entityGroups[assemblyHash] = assemblyGroupId;
          // Set sortIndex relative to the group's sortIndex (children come after the group)
          if (json.foundationAssemblies[assemblyId]) {
            json.foundationAssemblies[assemblyId] = {
              ...json.foundationAssemblies[assemblyId],
              sortIndex: assemblyGroupSortIndex + i + 1,
            };
          }
        }
      }

      // Create group for foundation components if there are any
      if (components.length > 0) {
        const componentGroupId = uid();
        maxGroupSortIndex += 1;
        const componentGroupSortIndex = maxGroupSortIndex;
        json.groups[componentGroupId] = {
          entityType: "GROUP",
          id: componentGroupId,
          name: `${settingsSetName}`,
          sortIndex: componentGroupSortIndex,
          parentId: undefined,
          disabled: false,
        };

        // Sort components alphanumerically by name
        const sortedComponents = [...components].sort((a, b) => {
          const nameA = json.foundationComponents[a]?.name ?? "";
          const nameB = json.foundationComponents[b]?.name ?? "";
          return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: "base" });
        });

        // Add entries to entityGroups and set sortIndex for each component
        for (let i = 0; i < sortedComponents.length; i++) {
          const componentId = sortedComponents[i];
          const componentHash = `${DocEntityPrefix.FoundationComponentEntity}.${componentId}`;
          json.entityGroups[componentHash] = componentGroupId;
          // Set sortIndex relative to the group's sortIndex (children come after the group)
          if (json.foundationComponents[componentId]) {
            json.foundationComponents[componentId] = {
              ...json.foundationComponents[componentId],
              sortIndex: componentGroupSortIndex + i + 1,
            };
          }
        }
      }
    }

    const builtInAssemblyGroupId = uid();
    maxGroupSortIndex += 1;
    const builtInAssemblyGroupSortIndex = maxGroupSortIndex;
    json.groups[builtInAssemblyGroupId] = {
      entityType: "GROUP",
      id: builtInAssemblyGroupId,
      name: "Built-in Foundation Assemblies",
      sortIndex: builtInAssemblyGroupSortIndex,
      parentId: undefined,
      disabled: false,
      isBuiltIn: true,
    };

    // Sort built-in assemblies alphanumerically by name
    const sortedBuiltInAssemblyIds = Object.keys(builtInFoundationAssemblies).sort((a, b) => {
      const nameA = builtInFoundationAssemblies[a]?.name ?? "";
      const nameB = builtInFoundationAssemblies[b]?.name ?? "";
      return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: "base" });
    });

    for (let i = 0; i < sortedBuiltInAssemblyIds.length; i++) {
      const assemblyId = sortedBuiltInAssemblyIds[i];
      const assemblyHash = `${DocEntityPrefix.FoundationAssembly}.${assemblyId}`;
      json.entityGroups[assemblyHash] = builtInAssemblyGroupId;
      // Set sortIndex relative to the group's sortIndex (children come after the group)
      if (json.foundationAssemblies[assemblyId]) {
        json.foundationAssemblies[assemblyId] = {
          ...json.foundationAssemblies[assemblyId],
          sortIndex: builtInAssemblyGroupSortIndex + i + 1,
        };
      }
    }

    const builtInComponentGroupId = uid();
    maxGroupSortIndex += 1;
    const builtInComponentGroupSortIndex = maxGroupSortIndex;
    json.groups[builtInComponentGroupId] = {
      entityType: "GROUP",
      id: builtInComponentGroupId,
      name: "Built-in Foundation Components",
      sortIndex: builtInComponentGroupSortIndex,
      parentId: undefined,
      disabled: false,
      isBuiltIn: true,
    };

    // Sort built-in components alphanumerically by name
    const sortedBuiltInComponentIds = Object.keys(builtInFoundationComponents).sort((a, b) => {
      const nameA = builtInFoundationComponents[a]?.name ?? "";
      const nameB = builtInFoundationComponents[b]?.name ?? "";
      return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: "base" });
    });

    for (let i = 0; i < sortedBuiltInComponentIds.length; i++) {
      const componentId = sortedBuiltInComponentIds[i];
      const componentHash = `${DocEntityPrefix.FoundationComponentEntity}.${componentId}`;
      json.entityGroups[componentHash] = builtInComponentGroupId;
      // Set sortIndex relative to the group's sortIndex (children come after the group)
      if (json.foundationComponents[componentId]) {
        json.foundationComponents[componentId] = {
          ...json.foundationComponents[componentId],
          sortIndex: builtInComponentGroupSortIndex + i + 1,
        };
      }
    }

    return json;
  },
};

export default migration_0_0_498;
